import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  Camera,
  MediaResult,
  MediaTypeSelection,
} from '@capacitor/camera';

/** Uses Android Photo Picker / iOS gallery on native — no READ_MEDIA_* permissions. */
@Injectable({ providedIn: 'root' })
export class NativeImagePickerService {
  usesNativeGalleryPicker(): boolean {
    return Capacitor.isNativePlatform();
  }

  async pickSingleImageFile(): Promise<File | null> {
    if (!this.usesNativeGalleryPicker()) {
      return null;
    }

    try {
      const { results } = await Camera.chooseFromGallery({
        mediaType: MediaTypeSelection.Photo,
        allowMultipleSelection: false,
        includeMetadata: true,
      });
      const item = results[0];
      if (!item?.webPath) {
        return null;
      }
      return await this.mediaResultToFile(item);
    } catch {
      return null;
    }
  }

  private async mediaResultToFile(item: MediaResult): Promise<File> {
    const response = await fetch(item.webPath!);
    const blob = await response.blob();
    const format = (item.metadata?.format ?? 'jpeg').toLowerCase();
    const ext = format === 'jpeg' ? 'jpg' : format;
    const mime =
      blob.type && blob.type !== 'application/octet-stream'
        ? blob.type
        : `image/${format === 'jpg' ? 'jpeg' : format}`;
    return new File([blob], `photo.${ext}`, { type: mime });
  }
}
